import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import { FaShoppingCart, FaHeart, FaWrench } from "react-icons/fa";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../auth/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { formatPrice, formatPriceCompact } from "../utils/currency";
import { getPartDetailPath } from "../utils/partDetailRoutes";
import { accountSettingsService } from "../services/apiService";

/**
 * Deep-link to main catalogue with vehicle + tier + optional category so the
 * classification table is available before opening PDP.
 * @param {object} product — normalized part (condition, origin, isOEM, categorySlug)
 * @param {string} vehicleQuery — optional `?vehicleMake=…` or `vehicleMake=…`
 */
export function buildCatalogListPath(product, vehicleQuery = "") {
  const normalizeToken = (value) =>
    String(value || "")
      .toLowerCase()
      .trim()
      .replace(/&/g, " and ")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
  const mapToClassificationCategory = (value) => {
    const t = normalizeToken(value);
    if (!t) return "";
    // Generic bucket names should not be forced into `category` query.
    if (["part", "parts", "spare", "spares", "products", "product"].includes(t)) return "";
    if (t.includes("brake")) return "brakes";
    if (t.includes("clutch")) return "clutch";
    if (t.includes("battery") || t.includes("electrical")) return "electrical_components";
    if (t.includes("filter")) return "filters";
    if (t.includes("cool")) return "cooling_system";
    if (t.includes("suspension")) return "suspension";
    if (t.includes("belt") || t.includes("chain") || t.includes("roller")) return "belts_chains_rollers";
    if (t.includes("engine")) return "engine";
    // Unknown/niche category tokens are safer as "no category filter"
    // so the classification table can still show compatible parts.
    return "";
  };

  const raw = String(vehicleQuery || "").replace(/^\?/, "");
  const params = new URLSearchParams(raw);
  const cond = String(product.condition || "").toLowerCase();
  if (cond === "used") {
    params.set("spareType", "used");
    params.delete("origin");
  } else if (product.origin === "OEM" || product.isOEM) {
    params.set("origin", "OEM");
    params.delete("spareType");
  } else {
    params.set("spareType", "new");
    params.delete("origin");
  }
  const slug = mapToClassificationCategory(product.categorySlug || product.category || product.subCategory);
  if (slug && String(slug).trim()) {
    params.set("category", String(slug).trim());
  }
  const deepSearch = String(product.partNumber || product.name || "").trim();
  if (deepSearch) {
    params.set("q", deepSearch);
  }
  const s = params.toString();
  return s ? `/catalog?${s}` : "/catalog";
}

/**
 * Theme-aware PDP-style part card (home catalogue strip, recommendations grid, etc.)
 * @param {"product"|"productRecord"|"catalog"} linkTarget — `catalog` opens
 * /catalog, `productRecord` opens the standard product details page, and the
 * default opens the catalog-listing PDP.
 */
const CatalogueDarkPartCard = ({
  product,
  index = 0,
  categoryData = null,
  linkTarget = "product",
  vehicleQuery = "",
  /** `grid` fills catalogue classification table cells; default `carousel` caps width. */
  layout = "carousel",
  /** When true, card uses dark chrome regardless of theme. Prefer relying on theme. */
  forceDark = false,
}) => {
  const [imgError, setImgError] = useState(false);
  const [wishlistItemId, setWishlistItemId] = useState("");
  const [wishlistBusy, setWishlistBusy] = useState(false);
  const { addToCart } = useCart();
  const { isAuthenticated } = useAuth();
  const { isDark: themeIsDark } = useTheme();
  const isDark = forceDark || themeIsDark;
  const navigate = useNavigate();

  const id = product.id || product._id;
  const productDetailPath = getPartDetailPath(product);
  const cardLinkTo =
    linkTarget === "catalog"
      ? buildCatalogListPath(product, vehicleQuery)
      : linkTarget === "productRecord" && id
        ? `/product/${id}`
        : productDetailPath;
  const discountPct =
    product.mrp && product.price && product.mrp > product.price
      ? Math.round(((product.mrp - product.price) / product.mrp) * 100)
      : null;
  const categoryLabel =
    product.subCategory || product.category || product.class || "Part";
  const oemLine = product.partNumber || product.sku || "—";
  const inStock = (product.stock ?? 0) > 0;
  const showImg = product.image && !imgError;
  const isUsed = String(product.condition || "").toLowerCase() === "used";
  const usedGrade = String(product.usedGrade || "").toUpperCase();
  const isGrid = layout === "grid";

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }
    try {
      await addToCart(
        {
          id,
          name: product.name,
          brand: product.brand,
          price: product.mrp || product.price,
          discountPrice:
            product.mrp && product.mrp > product.price ? product.price : null,
          imageUrl: product.image,
          partNumber: product.partNumber || product.sku,
          quantity: 1,
        },
        false
      );
      toast.success(`${product.name} added to cart!`);
    } catch {
      toast.error("Failed to add to cart");
    }
  };

  const handleToggleWishlist = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }
    if (wishlistBusy) return;

    setWishlistBusy(true);
    try {
      if (wishlistItemId) {
        await accountSettingsService.removeFromWishlist(wishlistItemId);
        setWishlistItemId("");
        toast.success("Removed from wishlist");
        return;
      }

      const rawProductId = String(product._id || product.id || "");
      const saved = await accountSettingsService.addToWishlist({
        partName: product.name || product.title || "Part",
        listingId: String(product.listingId || product.id || product._id || product.sku || ""),
        productId: /^[a-f\d]{24}$/i.test(rawProductId) ? rawProductId : undefined,
        nodeId: product.nodeId || product.catalogNodeId,
        catalogNodeId: product.catalogNodeId,
        catalogColumn: product.catalogColumn || product.column || "oem",
        vehicleId: product.vehicleId,
        partNumber: product.partNumber || product.sku,
        source: String(product.condition || "").toLowerCase() === "used" ? "used" : "new",
        price: product.price,
        notifyWhenAvailable: false,
      });
      setWishlistItemId(saved?.id || "");
      toast.success("Added to wishlist");
    } catch (error) {
      toast.error(error.message || "Could not update wishlist");
    } finally {
      setWishlistBusy(false);
    }
  };

  const linkState =
    linkTarget === "catalog"
      ? undefined
      : {
          product: { ...product, id, image: product.image },
          category: categoryData || { name: product.category || "Parts" },
        };

  if (isGrid) {
    return (
      <>
        {/* Mobile: compact square tile */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.02 }}
          className="sm:hidden w-full"
        >
          <Link
            to={cardLinkTo}
            state={linkState}
            className={`relative block aspect-[4/5] overflow-hidden rounded-lg border transition-transform active:scale-[0.98] ${
              isDark
                ? "border-slate-700/90 bg-[#0c1018] shadow-md shadow-black/25"
                : "border-gray-200 bg-white shadow-sm"
            }`}
          >
            {discountPct != null && (
              <span className="absolute top-1 right-1 z-10 rounded px-1 py-0.5 text-[8px] font-bold leading-none bg-emerald-600 text-white">
                {discountPct}%
              </span>
            )}
            {isUsed && usedGrade && (
              <span className="absolute top-1 left-1 z-10 rounded px-1 py-0.5 text-[7px] font-bold leading-none bg-amber-600/90 text-white">
                {usedGrade}
              </span>
            )}
            <div className="absolute inset-0 flex items-center justify-center p-2 pb-10">
              {showImg ? (
                <img
                  loading="lazy"
                  src={product.image}
                  alt={product.name}
                  className="max-h-full max-w-full object-contain"
                  onError={() => setImgError(true)}
                />
              ) : (
                <FaWrench className={`text-xl ${isDark ? "text-sky-400/80" : "text-blue-500"}`} aria-hidden />
              )}
            </div>
            <div
              className={`absolute inset-x-0 bottom-0 px-1.5 py-1.5 ${
                isDark
                  ? "bg-gradient-to-t from-slate-950 via-slate-950/95 to-transparent"
                  : "bg-gradient-to-t from-white via-white/95 to-transparent"
              }`}
            >
              <p
                className={`truncate text-[8px] font-semibold leading-tight ${
                  isDark ? "text-slate-200" : "text-gray-700"
                }`}
              >
                {product.name}
              </p>
              <p
                className={`mt-0.5 truncate text-[10px] font-bold leading-tight tabular-nums ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                {formatPriceCompact(product.price || 0)}
              </p>
            </div>
          </Link>
        </motion.div>

        {/* Tablet+: full card */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.04 }}
          className={`hidden sm:flex group w-full flex-col overflow-hidden h-full min-h-0 transition-all duration-300 max-w-none ${
            isDark
              ? "sp-prod-card pc shadow-sp-premium hover:-translate-y-0.5 rounded-2xl"
              : "rounded-xl border border-gray-100 bg-white shadow-lg shadow-gray-200/50"
          }`}
        >
          {renderGridCardBody({
            cardLinkTo,
            linkState,
            isDark,
            showImg,
            imgError,
            setImgError,
            categoryLabel,
            product,
            oemLine,
            discountPct,
            inStock,
            isUsed,
            usedGrade,
            handleAddToCart,
            handleToggleWishlist,
            inWishlist: Boolean(wishlistItemId),
            wishlistBusy,
          })}
        </motion.div>
      </>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.04 }}
      className={`group w-full flex flex-col overflow-hidden h-full min-h-0 transition-all duration-300 max-w-[18rem] mx-auto max-h-[28rem] ${
        isDark
          ? "sp-prod-card pc shadow-sp-premium hover:-translate-y-0.5 rounded-2xl"
          : "rounded-xl border border-gray-100 bg-white shadow-lg shadow-gray-200/50"
      }`}
    >
      {renderGridCardBody({
        cardLinkTo,
        linkState,
        isDark,
        showImg,
        imgError,
        setImgError,
        categoryLabel,
        product,
        oemLine,
        discountPct,
        inStock,
        isUsed,
        usedGrade,
        handleAddToCart,
        handleToggleWishlist,
        inWishlist: Boolean(wishlistItemId),
        wishlistBusy,
        isCarousel: true,
      })}
    </motion.div>
  );
};

function renderGridCardBody({
  cardLinkTo,
  linkState,
  isDark,
  showImg,
  setImgError,
  categoryLabel,
  product,
  oemLine,
  discountPct,
  inStock,
  isUsed,
  usedGrade,
  handleAddToCart,
  handleToggleWishlist,
  inWishlist,
  wishlistBusy,
  isCarousel = false,
}) {
  const isGridFull = !isCarousel;

  return (
    <>
      <Link
        to={cardLinkTo}
        state={linkState}
        className="flex flex-col flex-1 min-h-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/50 rounded-t-xl"
      >
        <div
          className={`relative flex items-center justify-center overflow-hidden transition-colors duration-300 ${
            isDark
              ? `sp-prod-img ${isGridFull ? "aspect-square w-full !h-auto p-2 mb-2" : "h-40 p-2"}`
              : `${isGridFull ? "aspect-square w-full p-2" : "h-40"} border-b bg-gray-50/50 border-gray-100 p-2`
          }`}
        >
          <button
            type="button"
            onClick={handleToggleWishlist}
            disabled={wishlistBusy}
            className={`absolute top-1.5 right-1.5 z-10 w-6 h-6 rounded-md border flex items-center justify-center transition-colors ${
              inWishlist
                ? "border-rose-500 bg-rose-500 text-white"
                : isDark
                  ? "bg-black/40 border-slate-600 text-slate-300 hover:text-rose-400"
                  : "bg-white/80 border-gray-200 text-gray-400 hover:text-rose-500 shadow-sm"
            } disabled:cursor-wait disabled:opacity-60`}
            aria-label={inWishlist ? "Remove from wishlist" : "Add to wishlist"}
          >
            <FaHeart className="text-xs" />
          </button>
          {showImg ? (
            <img
              loading="lazy"
              src={product.image}
              alt=""
              className="w-full h-full max-w-full max-h-full object-contain transform group-hover:scale-105 transition-transform duration-500"
              onError={() => setImgError(true)}
            />
          ) : (
            <div className="flex flex-col items-center justify-center text-center gap-2 text-slate-500 px-2">
              <FaWrench className={`text-2xl ${isDark ? "text-sky-400/90" : "text-blue-500"}`} aria-hidden />
              <span
                className={`text-[10px] sm:text-xs font-semibold line-clamp-2 ${
                  isDark ? "text-sky-300/95" : "text-gray-600"
                }`}
              >
                {categoryLabel}
              </span>
            </div>
          )}
        </div>

        <div className={`flex flex-col flex-1 gap-1.5 min-h-0 transition-colors duration-300 p-3 sm:p-4 ${isDark ? "" : "bg-white"}`}>
          <p className={`pc-brand text-[10px] font-bold uppercase tracking-wider ${isDark ? "text-sparepe-ink-3" : "text-gray-400"}`}>
            {product.brand || "Brand"}
          </p>
          <h3
            className={`pc-name font-medium line-clamp-2 leading-snug text-sm sm:text-[15px] min-h-[2rem] ${
              isDark ? "text-sparepe-ink" : "font-bold text-gray-800"
            }`}
          >
            {product.name}
          </h3>
          <p className={`pc-sku text-[9px] truncate font-mono ${isDark ? "text-sparepe-ink-3" : "text-gray-500"}`}>
            OEM: {oemLine}
          </p>

          <div className="pc-price-row flex flex-wrap items-baseline gap-x-1 gap-y-0.5 mt-0.5">
            <span
              className={`pc-price text-[15px] leading-none font-bold tabular-nums ${
                isDark ? "text-sparepe-ink" : "text-[22px] font-black text-blue-600"
              }`}
            >
              {formatPrice(product.price || 0)}
            </span>
            {product.mrp && product.mrp > product.price && (
              <>
                <span className={`text-[10px] line-through tabular-nums ${isDark ? "text-slate-500" : "text-gray-400"}`}>
                  {formatPrice(product.mrp)}
                </span>
                {discountPct != null && (
                  <span
                    className={`pc-disc text-[10px] font-semibold ${
                      isDark ? "sp-badge-success" : "font-bold text-green-600"
                    }`}
                  >
                    {discountPct}% off
                  </span>
                )}
              </>
            )}
          </div>

          <div className="flex flex-wrap gap-0.5 mt-0.5">
            {inStock && (
              <span className="text-[8px] font-bold uppercase px-1.5 py-0.5 rounded-md bg-emerald-600 text-white leading-none">
                In Stock
              </span>
            )}
            {isUsed ? (
              <span
                className={`text-[8px] font-bold uppercase px-1.5 py-0.5 rounded-md leading-none ${
                  isDark
                    ? "bg-amber-900/70 text-amber-100 border border-amber-600/40"
                    : "bg-amber-50 text-amber-700 border border-amber-100"
                }`}
              >
                {usedGrade ? `Grade ${usedGrade}` : "Used"}
              </span>
            ) : product.isOEM || product.origin === "OEM" ? (
              <span
                className={`text-[8px] font-bold uppercase px-1.5 py-0.5 rounded-md leading-none ${
                  isDark
                    ? "bg-blue-900/80 text-sky-100 border border-blue-700/50"
                    : "bg-blue-50 text-blue-700 border border-blue-100"
                }`}
              >
                OEM Fit
              </span>
            ) : (
              <span
                className={`text-[8px] font-bold uppercase px-1.5 py-0.5 rounded-md leading-none ${
                  isDark
                    ? "bg-amber-900/70 text-amber-100 border border-amber-600/40"
                    : "bg-amber-50 text-amber-700 border border-amber-100"
                }`}
              >
                1yr Warranty
              </span>
            )}
          </div>

          <p className={`text-[8px] mt-auto pt-1 ${isDark ? "text-slate-500" : "text-gray-500"}`}>
            Delivery:{" "}
            <span className={`font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>Tomorrow by 8pm</span>
          </p>
        </div>
      </Link>

      {!isUsed && (
        <div className={`pt-0 transition-colors duration-300 px-3 sm:px-4 pb-3 sm:pb-4 ${isDark ? "" : "bg-white"}`}>
          <button
            type="button"
            onClick={handleAddToCart}
            className={`w-full inline-flex items-center justify-center gap-1 text-[9px] font-semibold uppercase tracking-wide py-2.5 transition-all active:scale-95 ${
              isDark ? "sp-prod-add pc-add" : "rounded-lg bg-blue-600 text-white font-black shadow-md hover:bg-blue-700 shadow-blue-200"
            }`}
          >
            <FaShoppingCart className="text-sm" />
            Add to Cart
          </button>
        </div>
      )}
    </>
  );
}

export default CatalogueDarkPartCard;
